import { Component, Input, OnChanges } from '@angular/core';

import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import {
    FixtureParticipantPriceMap,
    GreyhoundParticipantDictionary,
    HorseRaceSortingDirection,
    MarketView,
    WinGameFixture,
    WinGameParticipant,
} from '@frontend/sports/racing/feature/models';

import { DropdownOption, OptionType } from '../../../../dropdown/models';
import { HorsesFixtureMarketHelper } from '../../../../horse-racing-shared/services/horses-fixture-market-helper.service';
import { HorsesFixtureParticipantHelper } from '../../../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../../../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { HorsesFixtureSortingHelper } from '../../../../horse-racing-shared/services/horses-fixture-sorting-helper.service';
import { TabBarItem } from '../../../../tab-bar/tab-bar.models';
import { GreyhoundTrackingService } from '../../../common/greyhound-tracking.service';
import { ParticipantsService } from '../../../common/participant-service';
import { HorsesRaceService } from '../../../services/horses-race.service';
import { RacingService } from '../../../services/racing-service';
import { BaseRaceWinMarketService } from '../../base/base-race-win-market.service';
import { GhBaseParticipantsComponent } from './gh-base-participants.component';

@Component({
    selector: 'ms-gh-win-market-participants',
    templateUrl: './gh-win-market-participants.component.html',
    standalone: false,
})
export class GhWinMarketParticipantsComponent extends GhBaseParticipantsComponent implements OnChanges {
    override isAllexpanded = false;
    dropDownItems: DropdownOption[];
    override activeOption: DropdownOption;
    @Input() participantOptionGroups: { [id: string]: EventOption[] };
    @Input() optionGroup: EventOptionGroup;

    @Input() selectedMarketTitle: string;
    @Input() greyhoundTrapDogMapping: GreyhoundParticipantDictionary = {};
    marketTabs: TabBarItem[];
    subMarkets: TabBarItem[];
    showMarketHelp: boolean;
    groupTypes = OptionGroupType;
    isOptionBet: boolean;
    marketViews = MarketView;

    constructor(
        public override sitecore: Sitecore,
        protected override horsesFixtureSortingHelper: HorsesFixtureSortingHelper,
        protected override horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        protected override horsesFixturePricingHelper: HorsesFixturePricingHelper,
        protected override racingService: RacingService,
        protected greyhoundTrackingService: GreyhoundTrackingService,
        protected override horsesFixtureMarketHelper: HorsesFixtureMarketHelper,
        protected override horsesRaceService: HorsesRaceService,
        protected override baseRaceWinMarketService: BaseRaceWinMarketService,
        protected override participantsService: ParticipantsService,
    ) {
        super(
            horsesFixturePricingHelper,
            racingService,
            horsesFixtureParticipantHelper,
            horsesFixtureSortingHelper,
            horsesFixtureMarketHelper,
            horsesRaceService,
            sitecore,
            baseRaceWinMarketService,
            participantsService,
        );
        this.dropDownItems = [
            { id: HorseRaceSortingDirection.Odds, name: 'Odds', type: OptionType.Regular },
            { id: HorseRaceSortingDirection.CardNumber, name: 'Race Card', type: OptionType.Regular },
        ];
    }

    ngOnChanges(changes: ISimpleChanges<GhWinMarketParticipantsComponent>): void {
        this.isOptionBet = changes.fixture?.currentValue.isOptionBet ?? false;
        const isselectedMarketTitleChanged =
            changes.selectedMarketTitle && changes.selectedMarketTitle?.currentValue !== changes.selectedMarketTitle?.previousValue;
        if (isselectedMarketTitleChanged) {
            this.isAllexpanded = false;
            this.vm.expandedParticipant = {};
        }
        this.setViewModel(changes.fixture?.firstChange || changes.fixture?.currentValue?.id !== changes.fixture?.previousValue?.id, this.fixture);
    }

    override setViewModel(isNewFixture: boolean, fixture: WinGameFixture): void {
        const { options, spOptions } = this.setOptions(fixture);
        this.setRunnersAndSort(options, spOptions, fixture, isNewFixture);
    }

    private setRunnersAndSort(
        options: FixtureParticipantPriceMap,
        spOptions: FixtureParticipantPriceMap,
        fixture: WinGameFixture,
        isNewFixture: boolean = false,
    ): void {
        let participants: WinGameParticipant[];
        if (isNewFixture) {
            const runners = this.horsesFixtureParticipantHelper.getParticipants(fixture.participants);
            this.vm.sortBy = this.baseRaceWinMarketService.getSortDirection(fixture, this.vm.allPricesLocked, this.vm.allPricesSP);
            participants = this.horsesFixtureSortingHelper.sortParticipantsByTrap(runners, options, this.vm.sortBy);
            this.isAllexpanded = false;
            this.vm.expandedParticipant = {};
        } else {
            participants = this.restoreAndFilter(this.participants);
        }
        this.activeOption = this.baseRaceWinMarketService.getActiveSortOption(this.vm.sortBy);
        this.participants = this.participantsService.buildRunners(participants, options, fixture, spOptions);
        this.isRunnerInfo = this.participants.some((participant) => this.setShowInfo(participant));
        this.vm.nonRunners = this.sortNonRunners(
            this.participantsService.buildRunners(this.horsesFixtureParticipantHelper.getNonRunnersList(fixture), options, fixture),
        );
        if (this.activeOption.id === HorseRaceSortingDirection.Odds) {
            this.orderParticipantsBy(HorseRaceSortingDirection.Odds, fixture, true);
        }
    }

    orderBy(sortBy: HorseRaceSortingDirection): void {
        this.activeOption.id = sortBy;
        this.orderParticipantsBy(sortBy, this.fixture, true);
        this.greyhoundTrackingService.trackSorting(this.fixture, sortBy);
    }

    toggleRunnerInfo(participant: WinGameParticipant): void {
        this.vm.expandedParticipant[participant.id] = !this.baseRaceWinMarketService.getExpandedState(participant, this.vm.expandedParticipant);
        if (this.participants.every((t) => this.vm.expandedParticipant[t.id])) this.isAllexpanded = true;
        else if (this.participants.every((t) => !this.vm.expandedParticipant[t.id])) this.isAllexpanded = false;

        this.greyhoundTrackingService.trackExpandCollapse(this.vm.expandedParticipant[participant.id], this.fixture, participant);
    }

    toggleExpandCollapse(): void {
        if (!this.isAllexpanded) {
            this.participants.forEach((participant) => {
                this.vm.expandedParticipant[participant.id] = true;
            });
            this.isAllexpanded = true;
        } else {
            this.participants.forEach((participant) => {
                this.vm.expandedParticipant[participant.id] = false;
            });
            this.isAllexpanded = false;
        }
        this.greyhoundTrackingService.trackExpandCollapse(this.isAllexpanded, this.fixture, null);
    }

    trackDropdownToggle(event: any): void {
        this.greyhoundTrackingService.trackSorting(this.fixture);
    }

    get isTrapWinner() {
        return this.marketType !== undefined && this.marketType === this.racingOptionMarketTypes.TrapWinner;
    }
}
